import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CRM_NAV_SHORTCUTS,
  CRM_SHORTCUTS_PANEL_TOGGLE_KEY,
  canUseCrmNavShortcut,
  isTypingInFormField
} from '../config/crmNavShortcuts';

const FLASH_MS = 2200;

function normalizeKey(key) {
  return String(key || '').length === 1 ? key.toUpperCase() : String(key || '');
}

export function useCrmNavShortcuts(user) {
  const navigate = useNavigate();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [flash, setFlash] = useState(null);
  const flashTimerRef = useRef(null);

  const availableShortcuts = useMemo(
    () => CRM_NAV_SHORTCUTS.filter((s) => canUseCrmNavShortcut(user, s)),
    [user]
  );

  const keyToShortcut = useMemo(() => {
    const map = new Map();
    availableShortcuts.forEach((shortcut) => {
      (shortcut.keys || []).forEach((key) => {
        map.set(normalizeKey(key), shortcut);
      });
    });
    return map;
  }, [availableShortcuts]);

  const showFlash = useCallback((shortcut) => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setFlash({
      label: shortcut.label,
      icon: shortcut.icon,
      keys: shortcut.keys
    });
    flashTimerRef.current = setTimeout(() => {
      setFlash(null);
      flashTimerRef.current = null;
    }, FLASH_MS);
  }, []);

  const goToShortcut = useCallback(
    (shortcut, { announce = true } = {}) => {
      if (!shortcut?.path) return false;
      if (!canUseCrmNavShortcut(user, shortcut)) return false;
      navigate(shortcut.path);
      if (announce) showFlash(shortcut);
      setIsPanelOpen(false);
      return true;
    },
    [navigate, showFlash, user]
  );

  const openPanel = useCallback(() => setIsPanelOpen(true), []);
  const closePanel = useCallback(() => setIsPanelOpen(false), []);
  const togglePanel = useCallback(() => setIsPanelOpen((v) => !v), []);

  useEffect(() => () => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
  }, []);

  useEffect(() => {
    if (!user) return undefined;

    const onKeyDown = (event) => {
      if (event.defaultPrevented) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTypingInFormField(event.target)) return;

      const key = normalizeKey(event.key);

      if (key === 'ESCAPE' && isPanelOpen) {
        event.preventDefault();
        closePanel();
        return;
      }

      if (key === CRM_SHORTCUTS_PANEL_TOGGLE_KEY || (event.shiftKey && key === '/')) {
        event.preventDefault();
        togglePanel();
        return;
      }

      const shortcut = keyToShortcut.get(key);
      if (!shortcut) return;

      event.preventDefault();
      goToShortcut(shortcut);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closePanel, goToShortcut, isPanelOpen, keyToShortcut, togglePanel, user]);

  return {
    availableShortcuts,
    isPanelOpen,
    openPanel,
    closePanel,
    togglePanel,
    goToShortcut,
    flash
  };
}
